// @ts-nocheck
/** @jsxRuntime classic */
/** @jsx tsx */
// ^^^ These lines disable typescript checking and revert to normal HTML attributes. Only use for custom widgets. ^^^

/**
 * Custom Legend allows setting out of box styling on Legend View or
 * Developer can add custom css to CSS object and replace or add classes
 * to desired elements.
 * 
 * TODO: Convert most functionality to a new Custom style template/Class 
 *       to allow multiple legend styles within the same app.
 */


import { subclass } from "@arcgis/core/core/accessorSupport/decorators";
import { tsx } from "@arcgis/core/widgets/support/widget";
import { VNode } from "@arcgis/core/widgets/support/interfaces";

import Legend from "@arcgis/core/widgets/Legend";

import { renderRelationshipRamp } from "@arcgis/core/widgets/Legend/styles/support/relationshipUtils";
import * as StyleUtils from "@arcgis/core/widgets/Legend/support/styleUtils";
import './CustomLegend.css';

const CSS = {
  widget: "esri-widget",
  base: "esri-legend esri-widget--panel",
  service: "esri-legend__service",
  label: "esri-legend__service-label",
  layer: "esri-legend__layer",
  groupLayer: "esri-legend__group-layer",
  groupLayerChild: "esri-legend__group-layer-child",
  layerTable: "esri-legend__layer-table",
  layerTableSizeRamp: "esri-legend__layer-table--size-ramp",
  layerChildTable: "esri-legend__layer-child-table",
  layerCaption: "esri-legend__layer-caption",
  layerBody: "esri-legend__layer-body",
  layerRow: "esri-legend__layer-row",
  layerCell: "esri-legend__layer-cell",
  layerInfo: "esri-legend__layer-cell esri-legend__layer-cell--info",
  imageryLayerStretchedImage: "esri-legend__imagery-layer-image--stretched",
  imageryLayerCellStretched: "esri-legend__imagery-layer-cell--stretched",
  imageryLayerInfoStretched: "esri-legend__imagery-layer-info--stretched",
  symbolContainer: "esri-legend__layer-cell esri-legend__layer-cell--symbols",
  symbol: "esri-legend__symbol",
  rampContainer: "esri-legend__ramps",
  sizeRamp: "esri-legend__size-ramp",
  colorRamp: "esri-legend__color-ramp",
  opacityRamp: "esri-legend__opacity-ramp",
  borderlessRamp: "esri-legend__borderless-ramp",
  rampTick: "esri-legend__ramp-tick",
  rampFirstTick: "esri-legend__ramp-tick-first",
  rampLastTick: "esri-legend__ramp-tick-last",
  rampLabelsContainer: "esri-legend__ramp-labels",
  rampLabel: "esri-legend__ramp-label",
  message: "esri-legend__message",
  header: "esri-widget__heading",
  hidden: "esri-hidden",

  // Custom Classes
  widgetWidth: "legend-widget__width",
  paddingBottomZero: "legend-widget__service-padding-bottom-zero",
  noBottomBorder: "legend-widget__no-bottom-border"
};

@subclass("app.widgets.CustomLegend")
export default class CustomLegend extends Legend {
  render(): VNode {
    this.style.render();
  }

  /**
   * Control Legend widget width, heigh, background-color, etc...
   * @returns Base Legend widget or Loading message
   */
  render(): VNode {
    const currentLayer = this.activeLayerInfos.toArray();
    const renderChildren = currentLayer.map(layer => this._renderLegendForLayer(layer)).filter(el => !!el);

    return (
      <div bind={this} class={this.classes(CSS.widget, CSS.base, CSS.widgetWidth)}>
        <div>
          {renderChildren.length
            ? renderChildren
            : <div class={CSS.message}>No Legend</div>
          }
        </div>
      </div>
    );
  }

  /**
   * Can be used to adjust styling for each group layer section, 
   * or single feature layer section. 
   * @param activeLayer : _esri.ActiveLayerInfo
   * 
   * @returns If layer has children, renders each child layer using recursion.
   * @returns If no children, returns legend item for layer
   */
   private _renderLegendForLayer(activeLayer: __esri.ActiveLayerInfo) {
    if (!activeLayer.ready) return null;

    const childrenLength = !!activeLayer.children.length;
    const key = `esri-legend__${activeLayer.layer.uid}-version-${activeLayer.version}`;

    if (childrenLength) {
      const children = activeLayer.children.toArray();
      const renderChildren = children.map(this._renderLegendForLayer.bind(this));

      return (
        <div key={key} class={this.classes(CSS.service, CSS.groupLayer, CSS.paddingBottomZero)}>
          <h3 class={this.classes(CSS.header, CSS.label)}>{activeLayer.title}</h3>
          {renderChildren}
        </div>
      );
    }
    const legendElements = activeLayer.legendElements;

    if (legendElements && !legendElements.length) return null;

    const renderLegendElements = legendElements.map(le => {
      return this._renderLegendForElement(le, activeLayer.layer);
    }).filter(el => !!el);

    if (!renderLegendElements.length) return null;

    const groupLayerChildStyle = {};
    groupLayerChildStyle[CSS.groupLayerChild] = !!activeLayer.parent;


    return (
      <div key={key} class={this.classes(CSS.service, groupLayerChildStyle, CSS.noBottomBorder)}>
        <h3 class={this.classes(CSS.header, CSS.label)}>{activeLayer.title}</h3>
        <div class={CSS.layer}>
          {renderLegendElements}
        </div>
      </div>
    );
  }

  /**
   * 
   * @param legendElement 
   * @param activeLayer 
   * @param childTable 
   *  Control styles of legend symbol container,
   * when symbol title is present
   *    @returns 
   * 
   *  Control style of legend symbold if no text is present
   */
  private _renderLegendForElement(
    legendElement: __esri.LegendElement,
    activeLayer: __esri.ActiveLayerInfo,
    childTable: boolean = false) {

    const colorRamp = "color-ramp" === legendElement.type;
    const opacityRamp = "opacity-ramp" === legendElement.type;
    const sizeRamp = "size-ramp" === legendElement.type;
    let renderLegendForElementInfo = null;

    if ("symbol-table" === legendElement.type || sizeRamp) {
      const renderLegendElementInfo = () => {
        return (
          <div bind={this} class={this.classes(CSS.layerBody)}>
            {legendElement.infos.map((i) => {
              return this._renderLegendForElementInfo(i, activeLayer, sizeRamp, legendElement.type);
            }).filter(el => !!el)}
          </div>
        );
      };

      renderLegendForElementInfo = renderLegendElementInfo();

    } else {
      const isRamp = "color-ramp" === legendElement.type
        || "opacity-ramp" === legendElement.type
        || "heatmap-ramp" === legendElement.type;

      renderLegendForElementInfo = isRamp ? this.renderLegendForRamp(legendElement)
        : "relationship-ramp" === legendElement.type
          ? renderRelationshipRamp(legendElement, this.id)
          : null;

    }

    if (!renderLegendForElementInfo) return null;

    const title = legendElement.title;
    let u = null;

    if ("string" == typeof title) {
      u = title;
    } else if (title) {
      const getTitle = StyleUtils.getTitle(title, colorRamp || opacityRamp);
      u = StyleUtils.isRendererTitle(title, colorRamp || opacityRamp) && title.title ? title.title + " (" + getTitle + ")" : getTitle;
    }

    const tableStyle = childTable ? CSS.layerChildTable : CSS.layerTable;
    const tableSizeStyle = {};
    tableSizeStyle[CSS.layerTableSizeRamp] = sizeRamp || !childTable;

    return (
      <div class={this.classes(tableStyle, tableSizeStyle)}>
        {u
          ? <div class={CSS.layerCaption}>
            {u}
          </div>
          : null
        }
        {renderLegendForElementInfo}
      </div>
    );
  }
  
  

  /**
   * Can be used to adjust styling and placement of legend symbols
   * @param infos 
   * @param activeLayer 
   * @param sizeRamp 
   * @param type 
   * @returns returns the symbol for legend element or title
   */
  private _renderLegendForElementInfo(
    infos: any,
    activeLayer: ActiveLayerInfo,
    sizeRamp: string,
    type: string) 
  {

    if (infos.type) return this._renderLegendForElement(infos, activeLayer, true);

    const isStretched = StyleUtils.isImageryStretchedLegend(activeLayer, type);

    const layerInfoStyle = {};
    layerInfoStyle[CSS.imageryLayerInfoStretched] = isStretched;

    const symbolContainerStyle = {};
    symbolContainerStyle[CSS.imageryLayerInfoStretched] = isStretched;
    symbolContainerStyle[CSS.sizeRamp] = !isStretched && sizeRamp;

    return (
      <div class={this.classes(CSS.symbolContainer, symbolContainerStyle)}>
        {infos.symbol && infos.preview
          ? <div class={this.classes(CSS.symbol)} bind={infos.preview} afterCreate={StyleUtils.attachToNode} ></div>
          : infos.src && this._renderImage(infos, activeLayer, isStretched)
        }
        <div class={this.classes(CSS.layerInfo, layerInfoStyle)}>
          {StyleUtils.getTitle(infos.label, !1) || ""}
        </div>
      </div>
    );
  }

  /**
   * Use if image is being used for rendered legend symbol
   * @param infos 
   * @param activeLayer 
   * @param sizeRamp 
   * @returns 
   */
  private _renderImage(
    infos: any,
    activeLayer: ActiveLayerInfo,
    sizeRamp: string) {

    const { label, src, opacity } = infos;
    const style = {};
    style[CSS.imageryLayerStretchedImage] = sizeRamp;
    style[CSS.symbol] = !sizeRamp;

    const styleOpacity = {
      opacity: "" + (null != opacity ? opacity : activeLayer.opacity)
    };

    return (
      <img
        src={src}
        alt={label}
        border={0}
        height={infos.height}
        width={infos.width}
        class={this.classes(style)}
        styles={styleOpacity} />
    );
  }
}